import { createReducer } from "typesafe-actions";

import { EntityType } from "@mapmycustomers/shared/enum";
import { RouteTravelMode } from "@mapmycustomers/shared/enum/routes/RouteTravelMode";
import { RouteType } from "@mapmycustomers/shared/enum/routes/RouteType";
import Address from "@mapmycustomers/shared/types/Address";
import { Activity, Company, MapEntity, Person } from "@mapmycustomers/shared/types/entity";
import Route, { RoutePreview } from "@mapmycustomers/shared/types/entity/Route";
import { RouteEdgeEntity } from "@mapmycustomers/shared/types/entity/RouteDetail";
import { GeoPoint } from "@mapmycustomers/shared/types/shapes";
import User, { UserRef } from "@mapmycustomers/shared/types/User";
import { isNotEmpty } from "@mapmycustomers/shared/util/assert";

import { formatDate } from "@app/util/formatters";
import { mapEntityIdGetter } from "@app/util/map/idGetters";
import { parseApiDate } from "@app/util/parsers";
import { DEFAULT_ALLOTTED_TIME_SECONDS } from "@app/util/route/constants";

import {
  Actions,
  addManualStop,
  addPinsToRoute,
  applyAllottedTimeToAllStops,
  buildRouteFromEditor,
  clearRouteDetails,
  createRoute,
  fetchScheduledActivities,
  initializeRouteCreate,
  initializeRouteEdit,
  insertStop,
  invertStops,
  removeManualStop,
  removePinsFromRoute,
  removeScheduledActivity,
  reorderStop,
  saveRoute,
  setSharedUsers,
  updateDetails,
  updateEndAddress,
  updateEndEdgeType,
  updateEndEntity,
  updateManualStop,
  updateRouteStops,
  updateStartAddress,
  updateStartEdgeType,
  updateStartEntity,
  updateStopDetails,
} from "../actions";
import { EditorRouteManualStop, EditorRouteStop } from "../types/RouteStop";
import reorderStopsByPreview from "../utils/reorderStopsByPreview";

import buildStopsFromRoute from "./utils/buildStopsFromRoute";
import companyEntityToRouteStop from "./utils/companyEntityToRouteStop";
import getEditorStopEntityId from "./utils/getEditorStopEntityId";
import personEntityToRouteStop from "./utils/personEntityToRouteStop";
import toRouteEditorSnapshot, { RouteEditorSnapshot } from "./utils/toRouteEditorSnapshot";
import withCompletedStopsLast from "./utils/withCompletedStopsLast";

export interface RouteEditorState {
  // Route-level Time per Stop, in seconds: the time new stops get, and the server's fallback
  // for stops without their own time.
  allottedTime: number;
  building: boolean;
  endAddress?: Address;
  endEntity?: RouteEdgeEntity;
  endEntityId?: RouteEdgeEntity["id"];
  endEntityType?: RouteEdgeEntity["entity"];
  endGeoPoint?: GeoPoint;
  initializing: boolean;
  // Snapshot of the user-editable fields exactly as seeded at Edit init; the
  // unsaved-changes check compares against it. Frozen here (not re-derived from
  // originalRoute at read time) so init-time seeding steps — e.g. the preview-order
  // reorder — can never diverge from the baseline again. Undefined in Create mode.
  initialSnapshot?: RouteEditorSnapshot;
  isPreviewStale: boolean;
  name: Route["name"];
  originalRoute?: Route;
  owner?: UserRef;
  routePreview?: RoutePreview;
  routeType: RouteType;
  saving: boolean;
  scheduledActivities: Activity[];
  scheduledActivitiesTotal: number;
  scheduledLoading: boolean;
  sharedUserIds: User["id"][];
  startAddress?: Address;
  startAt?: Date;
  startEntity?: RouteEdgeEntity;
  startEntityId?: RouteEdgeEntity["id"];
  startEntityType?: RouteEdgeEntity["entity"];
  startGeoPoint?: GeoPoint;
  stops: EditorRouteStop[];
  travelMode: RouteTravelMode;
}

export const initialEditorState: RouteEditorState = {
  allottedTime: DEFAULT_ALLOTTED_TIME_SECONDS,
  building: false,
  initializing: false,
  isPreviewStale: false,
  name: "",
  routeType: RouteType.OPTIMIZED,
  saving: false,
  scheduledActivities: [],
  scheduledActivitiesTotal: 0,
  scheduledLoading: false,
  sharedUserIds: [],
  stops: [],
  travelMode: RouteTravelMode.DRIVE,
};

const DEFAULT_NAME_DATE_FORMAT = "EEE, MMM do";

type CreateSuccessPayload = ReturnType<typeof initializeRouteCreate.success>["payload"];
type EditSuccessPayload = ReturnType<typeof initializeRouteEdit.success>["payload"];

const buildEmptyEditor = (payload: CreateSuccessPayload): RouteEditorState => ({
  allottedTime: DEFAULT_ALLOTTED_TIME_SECONDS,
  building: false,
  endAddress: undefined,
  endEntity: undefined,
  endEntityId: undefined,
  endEntityType: undefined,
  endGeoPoint: undefined,
  initializing: false,
  isPreviewStale: false,
  name: formatDate(payload.date, DEFAULT_NAME_DATE_FORMAT),
  owner: payload.owner,
  routePreview: undefined,
  routeType: RouteType.OPTIMIZED,
  saving: false,
  scheduledActivities: [],
  scheduledActivitiesTotal: 0,
  scheduledLoading: false,
  sharedUserIds: payload.owner ? [payload.owner.id] : [],
  startAddress: undefined,
  startAt: payload.date,
  startEntity: undefined,
  startEntityId: undefined,
  startEntityType: undefined,
  startGeoPoint: undefined,
  stops: [],
  travelMode: RouteTravelMode.DRIVE,
});

export const buildEditorFromRoute = ({
  route,
  routePreview,
}: EditSuccessPayload): RouteEditorState => ({
  allottedTime: route.routeDetail.allottedTime ?? DEFAULT_ALLOTTED_TIME_SECONDS,
  building: false,
  endAddress: route.routeDetail.endAddress ? { address: route.routeDetail.endAddress } : undefined,
  endEntity: route.routeDetail.endEntity ?? undefined,
  endEntityId: route.routeDetail.endEntityId ?? undefined,
  endEntityType: route.routeDetail.endEntityType ?? undefined,
  endGeoPoint: route.routeDetail.endGeoPoint ?? undefined,
  initializing: false,
  isPreviewStale: false,
  name: route.name,
  originalRoute: route,
  owner: route.user,
  routePreview,
  routeType: route.routeDetail.type,
  saving: false,
  scheduledActivities: [],
  scheduledActivitiesTotal: 0,
  scheduledLoading: false,
  sharedUserIds: route.userIds ?? [],
  startAddress: route.routeDetail.startAddress
    ? { address: route.routeDetail.startAddress }
    : undefined,
  startAt: parseApiDate(route.routeDetail.startAt),
  startEntity: route.routeDetail.startEntity ?? undefined,
  startEntityId: route.routeDetail.startEntityId ?? undefined,
  startEntityType: route.routeDetail.startEntityType ?? undefined,
  startGeoPoint: route.routeDetail.startGeoPoint ?? undefined,
  // Seed stops in the optimized preview order so the initial edit-page render
  // matches the arrival times and map markers (both numbered by preview order).
  // Without this, stops show in persisted route.stopTime (input) order until an
  // edit triggers buildRouteFromEditor.success, which reorders via the same util.
  // reorderStopsByPreview already tails checked-off stops when every remaining stop
  // matched the preview, but a partial match (or no preview at all) can leave one
  // stranded mid-list — withCompletedStopsLast is the backstop that always holds.
  // reorderStopsByPreview stamps displayOrder from its own sort order, which the
  // normalizer above can then reshuffle — re-stamp it from final position (same
  // 1-based scheme reorderStopsByPreview itself uses) since that util can't be
  // changed to normalize internally.
  stops: withCompletedStopsLast(
    reorderStopsByPreview(buildStopsFromRoute(route), routePreview)
  ).map((stop, index) => ({ ...stop, displayOrder: index + 1 })),
  travelMode: route.routeDetail.travelMode,
});

export const editorReducer = createReducer<RouteEditorState, Actions>(initialEditorState)
  .handleAction([initializeRouteCreate.request, initializeRouteEdit.request], (state) => ({
    ...state,
    initializing: true,
  }))
  .handleAction(initializeRouteCreate.success, (state, { payload }) => buildEmptyEditor(payload))
  .handleAction(initializeRouteEdit.success, (state, { payload }) => {
    const editor = buildEditorFromRoute(payload);
    return { ...editor, initialSnapshot: toRouteEditorSnapshot(editor) };
  })
  .handleAction([initializeRouteCreate.failure, initializeRouteEdit.failure], (state) => ({
    ...state,
    initializing: false,
  }))
  .handleAction(clearRouteDetails, () => initialEditorState)
  .handleAction(updateDetails, (state, action) => {
    const { payload } = action;
    // A stop without its own time falls back to the route value, so a new route value
    // changes what the preview computes for that stop.
    const changesFallbackStops =
      "allottedTime" in payload &&
      payload.allottedTime !== state.allottedTime &&
      state.stops.some((stop) => !isNotEmpty(stop.allottedTime));
    const invalidates =
      "routeType" in payload ||
      "startAt" in payload ||
      "travelMode" in payload ||
      changesFallbackStops;
    return {
      ...state,
      ...payload,
      isPreviewStale: invalidates ? true : state.isPreviewStale,
    };
  })
  .handleAction(updateStartAddress.success, (state, action) => ({
    ...state,
    isPreviewStale: true,
    startAddress: action.payload.address,
    startEntity: undefined,
    startEntityId: undefined,
    startEntityType: undefined,
    startGeoPoint: action.payload.geoPoint,
  }))
  .handleAction(updateEndAddress.success, (state, action) => ({
    ...state,
    endAddress: action.payload.address,
    endEntity: undefined,
    endEntityId: undefined,
    endEntityType: undefined,
    endGeoPoint: action.payload.geoPoint,
    isPreviewStale: true,
  }))
  .handleAction(updateStartEdgeType, (state, { payload }) =>
    payload === "address"
      ? {
          ...state,
          isPreviewStale: true,
          startEntity: undefined,
          startEntityId: undefined,
          startEntityType: undefined,
        }
      : {
          ...state,
          isPreviewStale: true,
          startAddress: undefined,
          startEntity: undefined,
          startEntityId: undefined,
          startEntityType: payload,
          startGeoPoint: undefined,
        }
  )
  .handleAction(updateEndEdgeType, (state, { payload }) =>
    payload === "address"
      ? {
          ...state,
          endEntity: undefined,
          endEntityId: undefined,
          endEntityType: undefined,
          isPreviewStale: true,
        }
      : {
          ...state,
          endAddress: undefined,
          endEntity: undefined,
          endEntityId: undefined,
          endEntityType: payload,
          endGeoPoint: undefined,
          isPreviewStale: true,
        }
  )
  .handleAction(updateStartEntity.success, (state, { payload }) => ({
    ...state,
    isPreviewStale: true,
    startAddress: payload.entity.address ? { address: payload.entity.address } : undefined,
    startEntity: payload.entity,
    startEntityId: payload.entity.id,
    startEntityType: payload.entity.entity,
    startGeoPoint: payload.entity.geoPoint ?? undefined,
  }))
  .handleAction(updateEndEntity.success, (state, { payload }) => ({
    ...state,
    endAddress: payload.entity.address ? { address: payload.entity.address } : undefined,
    endEntity: payload.entity,
    endEntityId: payload.entity.id,
    endEntityType: payload.entity.entity,
    endGeoPoint: payload.entity.geoPoint ?? undefined,
    isPreviewStale: true,
  }))
  .handleAction(removeScheduledActivity, (state, action) => ({
    ...state,
    isPreviewStale: true,
    scheduledActivities: state.scheduledActivities.filter(
      (activity) => activity.id !== action.payload
    ),
  }))
  .handleAction(fetchScheduledActivities.request, (state) => ({
    ...state,
    scheduledLoading: true,
  }))
  .handleAction(fetchScheduledActivities.success, (state, action) => ({
    ...state,
    isPreviewStale: true,
    scheduledActivities: action.payload,
    scheduledActivitiesTotal: action.payload.length,
    scheduledLoading: false,
  }))
  .handleAction(fetchScheduledActivities.failure, (state) => ({
    ...state,
    scheduledLoading: false,
  }))
  .handleAction(addPinsToRoute, (state, action) => {
    const existingKeys = new Set(
      state.stops.map((stop) =>
        mapEntityIdGetter({ id: getEditorStopEntityId(stop), entity: stop.entity } as MapEntity)
      )
    );
    const baseDisplayOrder = state.stops.length;
    const newStops = action.payload.pins
      .filter((pin) => !existingKeys.has(mapEntityIdGetter(pin)))
      .filter((pin) => pin.entity === EntityType.COMPANY || pin.entity === EntityType.PERSON)
      .map((pin, index) =>
        pin.entity === EntityType.PERSON
          ? personEntityToRouteStop(pin as Person, baseDisplayOrder + index + 1, state.allottedTime)
          : companyEntityToRouteStop(
              pin as Company,
              baseDisplayOrder + index + 1,
              state.allottedTime
            )
      );
    return {
      ...state,
      isPreviewStale: true,
      // New pins are never checked off, so appending them raw could land them after an
      // existing completed suffix — renormalize so they join the remaining stops instead.
      // Renumber afterward: normalizing can move existing stops relative to the new
      // ones, so neither their old displayOrder nor the new ones' length-based guess
      // (above) still matches final position.
      stops: withCompletedStopsLast([...state.stops, ...newStops]).map((stop, index) => ({
        ...stop,
        displayOrder: index + 1,
      })),
    };
  })
  .handleAction(removePinsFromRoute, (state, action) => {
    const removeKeys = new Set(action.payload.pinIds);
    const renumbered = state.stops
      .filter(
        (stop) =>
          !removeKeys.has(
            mapEntityIdGetter({
              id: getEditorStopEntityId(stop),
              entity: stop.entity,
            } as MapEntity)
          )
      )
      .map((stop, index) => ({ ...stop, displayOrder: index + 1 }));
    return {
      ...state,
      isPreviewStale: true,
      stops: renumbered,
    };
  })
  .handleAction(updateStopDetails, (state, { payload }) => {
    const updatedStops = state.stops.map((stop) =>
      stop.entity === payload.entityType && getEditorStopEntityId(stop) === payload.entityId
        ? { ...stop, ...payload.details }
        : stop
    );
    return {
      ...state,
      isPreviewStale: true,
      // This is the Mark Done / Mark Undone handler — a stop can cross the completed
      // boundary right here, so it's the write that most needs to re-settle the suffix.
      stops: withCompletedStopsLast(updatedStops),
    };
  })
  .handleAction(applyAllottedTimeToAllStops, (state, { payload: allottedTime }) => {
    const stops = state.stops.map((stop) =>
      // An activity-linked stop keeps its appointment length.
      isNotEmpty(stop.syncedActivityId) || stop.allottedTime === allottedTime
        ? stop
        : { ...stop, allottedTime }
    );
    const changed = stops.some((stop, index) => stop !== state.stops[index]);
    return {
      ...state,
      allottedTime,
      isPreviewStale: changed || state.isPreviewStale,
      stops: changed ? stops : state.stops,
    };
  })
  .handleAction(updateRouteStops.success, (state, action) => {
    const { addEntities = [], entityType, removeEntityIds = [] } = action.payload;
    // removeEntityIds/addEntities belong to a single payload.entityType (Company XOR Person);
    // scope matching to that type so manual-stop pivot ids (a different id-namespace) can't collide.
    const removeSet = new Set<EditorRouteStop["id"]>(removeEntityIds);
    const existingEntityIds = new Set<EditorRouteStop["id"]>(
      state.stops.filter((stop) => stop.entity === entityType).map(getEditorStopEntityId)
    );
    const filtered = state.stops.filter(
      (stop) => stop.entity !== entityType || !removeSet.has(getEditorStopEntityId(stop))
    );
    const newStops = addEntities
      .filter((entity) => !existingEntityIds.has(entity.id))
      .map((entity, index) =>
        entityType === EntityType.PERSON
          ? personEntityToRouteStop(entity as Person, index + 1, state.allottedTime)
          : companyEntityToRouteStop(entity as Company, index + 1, state.allottedTime)
      );
    return {
      ...state,
      isPreviewStale: true,
      // Normalize before renumbering: the freshly-added entities are never checked off, so
      // appending them after `filtered` could otherwise land them past a completed suffix.
      stops: withCompletedStopsLast([...filtered, ...newStops]).map((stop, index) => ({
        ...stop,
        displayOrder: index + 1,
      })),
    };
  })
  .handleAction(insertStop.success, (state, action) => {
    const { entity, entityType, insertAt } = action.payload;
    const clampedIndex = Math.max(0, Math.min(insertAt, state.stops.length));
    const newStop =
      entityType === EntityType.PERSON
        ? personEntityToRouteStop(entity as Person, clampedIndex + 1, state.allottedTime)
        : companyEntityToRouteStop(entity as Company, clampedIndex + 1, state.allottedTime);
    const updatedStops = [
      ...state.stops.slice(0, clampedIndex),
      newStop,
      ...state.stops.slice(clampedIndex).map((stop) => ({
        ...stop,
        displayOrder: stop.displayOrder + 1,
      })),
    ];
    return {
      ...state,
      isPreviewStale: true,
      // The UI clamps insertAt to the remaining count, but the reducer stays correct
      // regardless of caller behavior — a new (never checked-off) stop can't be left
      // stranded inside or after the completed suffix. Renumber afterward: an
      // out-of-range insertAt would otherwise leave the shifted-by-one displayOrder
      // values above stale once normalizing moves stops relative to each other.
      stops: withCompletedStopsLast(updatedStops).map((stop, index) => ({
        ...stop,
        displayOrder: index + 1,
      })),
    };
  })
  .handleAction(addManualStop, (state, { payload }) => {
    const { insertAt, manualAddress, manualGeoPoint, manualName } = payload;
    const { stops } = state;
    const clampedIndex = Math.max(0, Math.min(insertAt, stops.length));
    const tempId =
      Math.min(
        0,
        ...stops.map((stop) => stop.id).filter((id): id is number => typeof id === "number")
      ) - 1;
    const newStop: EditorRouteManualStop = {
      id: tempId,
      allottedTime: state.allottedTime,
      checkedOff: false,
      displayOrder: clampedIndex + 1,
      entity: null,
      manualAddress,
      manualGeoPoint,
      manualName,
    };
    const updatedStops = [
      ...stops.slice(0, clampedIndex),
      newStop,
      ...stops
        .slice(clampedIndex)
        .map((stop) => ({ ...stop, displayOrder: stop.displayOrder + 1 })),
    ];
    return {
      ...state,
      isPreviewStale: true,
      // Same defensive reasoning as insertStop.success: the new manual stop is never
      // checked off, so it must not end up stranded inside/after the completed suffix.
      // Renumber afterward for the same reason too — an out-of-range insertAt would
      // otherwise leave stale shifted-by-one displayOrder values.
      stops: withCompletedStopsLast(updatedStops).map((stop, index) => ({
        ...stop,
        displayOrder: index + 1,
      })),
    };
  })
  .handleAction(updateManualStop, (state, { payload }) => {
    const { id, manualAddress, manualGeoPoint, manualName } = payload;
    const updatedStops = state.stops.map((stop) =>
      stop.entity === null && stop.id === id
        ? { ...stop, manualAddress, manualGeoPoint, manualName }
        : stop
    );
    return {
      ...state,
      isPreviewStale: true,
      stops: updatedStops,
    };
  })
  .handleAction(removeManualStop, (state, { payload }) => {
    const renumbered = state.stops
      .filter((stop) => !(stop.entity === null && stop.id === payload.id))
      .map((stop, index) => ({ ...stop, displayOrder: index + 1 }));
    return {
      ...state,
      isPreviewStale: true,
      stops: renumbered,
    };
  })
  .handleAction(reorderStop, (state, { payload }) => {
    const { fromIndex, toIndex } = payload;
    const { stops } = state;
    // The completed suffix starts here (stops.length if there is none). A `toIndex` at or
    // past it would drag a remaining stop into the completed block, and a `fromIndex`
    // pointing at a completed stop would drag something the sidebar gives no handle to
    // (Step 5 removes it there) — reject both so an out-of-range action can't produce a
    // visible snap-back once withCompletedStopsLast re-settles the array below.
    const firstCompletedIndex = stops.findIndex((stop) => stop.checkedOff);
    const completedBoundary = firstCompletedIndex === -1 ? stops.length : firstCompletedIndex;

    if (
      fromIndex === toIndex ||
      fromIndex < 0 ||
      fromIndex >= stops.length ||
      toIndex < 0 ||
      toIndex >= stops.length ||
      toIndex >= completedBoundary ||
      stops[fromIndex]?.checkedOff
    ) {
      return state;
    }

    const reordered = [...stops];
    reordered.splice(toIndex, 0, reordered.splice(fromIndex, 1)[0]);

    const renumbered = withCompletedStopsLast(reordered).map((stop, index) => ({
      ...stop,
      displayOrder: index + 1,
    }));

    return {
      ...state,
      isPreviewStale: true,
      stops: renumbered,
    };
  })
  .handleAction(invertStops, (state) => {
    // Reversing the whole array would put the completed block first — reassert the suffix
    // before renumbering (partition only; each handler keeps its own renumbering scheme).
    const renumbered = withCompletedStopsLast([...state.stops].reverse()).map((stop, index) => ({
      ...stop,
      displayOrder: index + 1,
    }));

    return {
      ...state,
      isPreviewStale: true,
      stops: renumbered,
    };
  })
  .handleAction(setSharedUsers, (state, { payload }) => ({
    ...state,
    sharedUserIds: payload,
  }))
  .handleAction(buildRouteFromEditor.request, (state) => ({ ...state, building: true }))
  .handleAction(buildRouteFromEditor.success, (state, action) => {
    // Edit mode (entered via `/route/edit/:id`, indicated here by the presence
    // of `originalRoute`) reorders the editor's stops to match the optimized
    // preview order. Create mode has neither URL nor `originalRoute`; its map
    // ordering goes through `getRouteEditorOrderedStopsForCreate` (mixes
    // scheduledActivities + stops by preview order), so reordering the slice
    // would be redundant.
    const reorderedStops = state.originalRoute
      ? reorderStopsByPreview(state.stops, action.payload)
      : state.stops;
    return {
      ...state,
      building: false,
      isPreviewStale: false,
      routePreview: action.payload,
      // Same backstop as the init-time seeding above: a partial preview match can leave a
      // checked-off stop mid-list even after reorderStopsByPreview's own tailing. Re-stamp
      // displayOrder from final position afterward, same as buildEditorFromRoute above —
      // reorderStopsByPreview's own displayOrder no longer matches once normalizing
      // reshuffles it (a no-op in Create mode, where reorderedStops is just state.stops).
      stops: withCompletedStopsLast(reorderedStops).map((stop, index) => ({
        ...stop,
        displayOrder: index + 1,
      })),
    };
  })
  .handleAction(buildRouteFromEditor.failure, (state) => ({ ...state, building: false }))
  .handleAction(createRoute.request, (state) => ({ ...state, saving: true }))
  .handleAction(createRoute.success, () => initialEditorState)
  .handleAction(createRoute.failure, (state) => ({ ...state, saving: false }))
  .handleAction(saveRoute.request, (state) => ({ ...state, saving: true }))
  .handleAction(saveRoute.success, () => initialEditorState)
  .handleAction(saveRoute.failure, (state) => ({ ...state, saving: false }));

export default editorReducer;
